/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { JobListItem } from './job.model';
import {
  isJobEditable,
  jobCsCreateDialogRoute,
  jobSpecCreateDialogRoute,
} from '@gh/builder-shared';
import { AppRouter, UserContext } from '@gh/common-shared';
import { BuilderPermission, BuilderPermissionSet } from '@gh/builder-shared-data';
import { filter$ } from '@gh/rx/operators';
import { DialogService, NotificationService } from '@gh/core-ui';

@Injectable()
export class JobNavigationService {

  constructor(private router: AppRouter,
              private userContext: UserContext,
              private notifications: NotificationService,
              private dialogs: DialogService) {
  }

  onJobChangeOrderOpen(job: JobListItem, title: string): void {
    this.router.navigateToChild([`/job-admin/job-change-orders`, job.id], {
      parentTitle: title,
    });
  }

  onJobOrderOpen(job: JobListItem, title: string): void {
    this.router.navigateToChild(['/job-admin/job-orders', job.id], {
      parentTitle: title,
    });
  }

  onFinancialsOpen(job: JobListItem, title?: string): void {
    this.router.navigateToChild(['job-admin', 'job-financials', job.id], {
      parentTitle: title,
    });
  }

  onJobEstimateOpen(job: JobListItem, title?: string): void {
    this.router.navigateToChild(['job-admin/jobs', job.id, 'estimate'], {
      parentTitle: title,
    });
  }

  onColorSelectionOpen(job: JobListItem, title: string): void {
    if (job.isCsExist) {
      this.router.navigateToChild(['job-admin/jobs', job.id, 'color-selection'], { parentTitle: title });
    } else if (!isJobEditable(job)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT)) {
      this.notifications.error('msg_cs_empty');
    } else {
      this.dialogs.openWithInput(jobCsCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((csm: any) =>
          this.router.navigateToChild(
           ['job-admin/jobs', job.id, 'color-selection', 'new'],
           {
             queryParams: { csMasterId: csm.master.id },
             parentTitle: title,
           }));
    }
  }

  onJobSpecificationOpen(job: JobListItem, title: string): void {
    if (job.isSpecExist) {
      this.router.navigateToChild([`/job-admin/jobs/${job.id}/spec`], {  queryParams: { forDpp: job.isDpp }, parentTitle: title });
    } else if (!isJobEditable(job)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)) {
      this.notifications.error('msg_spec_empty');
    } else {
      this.dialogs.openWithInput(jobSpecCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((formValue: any) => {
          this.router.navigateToChild(
            [`/job-admin/jobs/${job.id}/spec/new`],
            {
              queryParams: { specTemplateId: formValue.jobSpecTemplate.id, forDpp: job.isDpp },
              parentTitle: title,
            });
        });
    }
  }

  onJobDetailsOpen(job: JobListItem, title: string): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobDetails.ANY)) {
      if (job.isDpp) {
        this.router.navigateToChild(['/job-admin/jobs/dpp', job.id], {
          parentTitle: title,
        });
      } else {
        this.router.navigateToChild(['/job-admin/jobs', job.id], {
          parentTitle: title,
        });
      }
    }
  }

}
